import React, { useEffect, useState } from 'react';
import VersionList from 'shared/components/ui/VersionList';
import { formatDateTime } from './formParts';

/**
 * Every version of the eval, newest first. A version is frozen: viewing one
 * puts the editor in read-only preview; "Restore" creates a NEW version equal
 * to it (the history never rewrites). One version carries the `production`
 * label and is the one that runs; "Set as production" moves the label
 * (TRO-461). Results are pinned to the version that scored them, so the
 * history is also the legend for the results chart.
 *
 * Two homes, one list. In the editor it is an inspector pinned to the canvas,
 * beside the thing being edited, like the step panel. On the results page
 * there is no canvas to pin to, so it takes the app's own side slot and the
 * page shrinks beside it rather than disappearing under it.
 */

function ago(iso) {
  if (!iso) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return 'just now';
  const m = Math.floor(secs / 60); if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d} d ago`;
  return formatDateTime(iso);
}

/** Relative while it is recent; the exact time on hover, in the app's one date format. */
function When({ iso }) {
  if (!iso) return null;
  return <span className="tabular-nums" title={formatDateTime(iso)}>{ago(iso)}</span>;
}

const BACKFILL_LABEL = {
  awaiting_confirmation: 'waiting for your go-ahead', queued: 'queued', running: 'running',
  completed: 'done', cancelled: 'cancelled', failed: 'failed',
};

function who(createdBy) {
  if (!createdBy) return '';
  if (createdBy.startsWith('user:')) return 'you / a teammate';
  return createdBy;
}

export default function VersionHistoryPanel({
  versions, currentVersion, editingVersion = null, previewVersion, dirty, acting, onPreview, onRestore, onSetProduction, onClose,
  backfills = [], onConfirmBackfill, onCancelBackfill,
}) {
  const boxRef = React.useRef(null);
  const [pos, setPos] = useState({ top: 44, right: 16 });
  // Under the Versions button, whichever header it is in. A click anywhere else, or Escape, closes it.
  useEffect(() => {
    const t = document.querySelector('[data-versions-trigger]');
    if (t) { const r = t.getBoundingClientRect(); setPos({ top: Math.round(r.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - r.right)) }); }
    const down = (e) => {
      if (boxRef.current?.contains(e.target) || e.target.closest?.('[data-versions-trigger]')) return;
      onClose?.();
    };
    const key = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', down);
    window.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); window.removeEventListener('keydown', key); };
  }, [onClose]);
  const nextVersion = Math.max(0, ...(versions || []).map((v) => v.version)) + 1;
  const body = (
    <>
      {/* The same header as the app's other popups (the step palette, search): a title, then the list. */}
      <div className="flex h-10 shrink-0 items-center border-b border-[color:var(--border-default)] px-4">
        <div className="text-[12.5px] font-semibold text-[color:var(--text-primary)]">Versions</div>
      </div>
      {dirty && (
        <div className="shrink-0 border-b border-[color:var(--border-subtle)] px-4 py-2.5 text-[12px] text-[color:var(--text-muted)]">
          Unsaved changes. Save to make v{nextVersion}.
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <VersionList
          liveLabel="In production"
          items={(versions || []).map((v) => {
            const isCurrent = v.version === currentVersion;
            const isEditing = editingVersion != null && v.version === editingVersion && !isCurrent;
            const isPreview = previewVersion === v.version;
            const bf = (backfills || []).find((b) => b.version === v.version);
            const actions = [
              { key: 'view', label: isPreview ? 'Exit preview' : 'View', onClick: () => onPreview(isPreview ? null : v.version) },
              !isCurrent && onSetProduction && {
                key: 'prod', label: 'Push to production', ariaLabel: `Set v${v.version} as production`,
                disabled: acting != null, busy: acting === `prod:${v.version}`, onClick: () => onSetProduction(v.version),
              },
              !isCurrent && onRestore && {
                key: 'restore', label: 'Restore', title: 'A new version equal to this one, not in production until you set it',
                disabled: acting != null, busy: acting === v.version, onClick: () => onRestore(v.version),
              },
            ];
            return {
              id: v.version,
              label: `v${v.version}${isEditing ? ' (editing)' : ''}`,
              live: isCurrent,
              active: isPreview,
              time: <When iso={v.created_at} />,
              author: who(v.created_by),
              note: v.change_note,
              actions,
              extra: bf && (
                <div data-testid={`backfill-v${v.version}`} className="mt-1 text-[11.5px] text-[color:var(--text-secondary)]">
                  Re-scoring history: {BACKFILL_LABEL[bf.status] || bf.status}
                  {bf.status !== 'awaiting_confirmation' && ` · ${bf.processed} of ${bf.estimate?.units_in_window ?? '?'} ${bf.estimate?.unit_word || ''}`}
                  {bf.units ? ` · ${bf.units} units` : ''}
                  {bf.status === 'awaiting_confirmation' && bf.confirm_reason && <div className="mt-0.5 text-[color:var(--text-muted)]">{bf.confirm_reason}</div>}
                  {['awaiting_confirmation', 'queued', 'running'].includes(bf.status) && (
                    <div className="mt-1 flex gap-3">
                      {bf.status === 'awaiting_confirmation' && (
                        <button type="button" onClick={() => onConfirmBackfill?.(bf.id)} className="font-medium text-[color:var(--text-primary)] hover:underline">Re-score</button>
                      )}
                      <button type="button" onClick={() => onCancelBackfill?.(bf.id)} className="font-medium text-[color:var(--text-primary)] hover:underline">Cancel</button>
                    </div>
                  )}
                </div>
              ),
            };
          })}
        />
      </div>
    </>
  );

  return (
    <aside aria-label="Version history" ref={boxRef} style={{ position: 'fixed', top: pos.top, right: pos.right, fontFamily: 'Inter, sans-serif' }}
      className="z-[70] flex max-h-[min(70vh,560px)] w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-token-lg">
      {body}
    </aside>
  );
}
